import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Room, RoomEvent, RemoteTrack, RemoteTrackPublication, Participant, ConnectionState, Track } from 'livekit-client';
import { serverConfig } from '../../utils/moderation.ts'; // Asegúrate que la ruta sea correcta
import { Link } from 'react-router-dom';
import { FaSpinner, FaEyeSlash, FaEye } from 'react-icons/fa';
import { imageUrl, projectName, coinNames } from '../../utils/constants.ts'; // Asumiendo que están aquí

interface StreamPreviewCardProps {
  tokenAddress: string;
  ownerAddress: string;
  chainId: number; // <-- Add chainId prop
  onInactive?: () => void; // new prop
  // Añade props opcionales si vienen de la API
  // tokenName?: string;
  // viewerCount?: number;
}

const StreamPreviewCard: React.FC<StreamPreviewCardProps> = ({
  tokenAddress,
  ownerAddress,
  chainId, // <-- Destructure chainId
  onInactive,
  // tokenName: initialTokenName, // Recibe nombre inicial si existe
  // viewerCount: initialViewerCount // Recibe viewers iniciales si existen
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const roomRef = useRef<Room | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showVideo, setShowVideo] = useState(false);
  const [hasTrack, setHasTrack] = useState(false);
  const [viewerCount, setViewerCount] = useState<number>(0);  // nuevo state
  // const [tokenInfo, setTokenInfo] = useState<{ name: string, symbol: string } | null>(null); // Para info del token si la buscas aquí

  const logPrefix = `[PreviewCard ${tokenAddress.slice(0, 6)}]`;

  // Función estable para conectar
  const connectToRoom = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setShowVideo(false);

    if (roomRef.current) {
      await roomRef.current.disconnect();
      roomRef.current = null;
    }

    const room = new Room({
      adaptiveStream: true,
      dynacast: true,
    });
    roomRef.current = room;

    // Listener para estado de conexión
    room.on(RoomEvent.ConnectionStateChanged, (state) => {
      console.log(logPrefix, "Connection State:", state);
      setIsConnected(state === ConnectionState.Connected);
      if (state === ConnectionState.Disconnected) {
        setShowVideo(false); // Ocultar video si se desconecta
        // Podrías intentar reconectar o marcar como offline
      }
      if (state === ConnectionState.Connected) {
         // Buscar owner y track al conectar
         const owner = room.getParticipantByIdentity(ownerAddress);
         if (owner) handleOwnerParticipant(owner);
      }
    });

    // Listener para cuando un track se suscribe (y está listo para mostrar)
    room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
      if (participant.identity === ownerAddress && track.kind === Track.Kind.Video && videoRef.current) {
        console.log(logPrefix, "Owner video track subscribed:", track.sid);
        track.attach(videoRef.current);
        setShowVideo(true);
        setIsLoading(false); // Dejar de cargar cuando el video se adjunta
      }
    });

    // Listener para cuando un track se deja de publicar
    room.on(RoomEvent.TrackUnpublished, (publication, participant) => {
       if (participant.identity === ownerAddress && publication.kind === Track.Kind.Video) {
           console.log(logPrefix, "Owner video track unpublished.");
           setShowVideo(false);
           // Podrías mostrar un mensaje "Stream ended"
       }
    });

    // Listener para cuando el owner se desconecta
    room.on(RoomEvent.ParticipantDisconnected, (participant) => {
        if (participant.identity === ownerAddress) {
            console.log(logPrefix, "Owner disconnected.");
            setShowVideo(false);
            setError("Stream ended");
            setIsLoading(false);
        }
    });

    // Función interna para manejar al participante owner
    const handleOwnerParticipant = (participant: Participant) => {
        let videoAttached = false;
        participant.videoTrackPublications.forEach(pub => {
            if (pub.isSubscribed && pub.track && videoRef.current) {
                console.log(logPrefix, "Found subscribed owner video track on connect:", pub.trackSid);
                pub.track.attach(videoRef.current);
                setShowVideo(true);
                videoAttached = true;
            }
        });
        if (videoAttached) setIsLoading(false);
        // Si no hay video aún, esperamos al evento TrackSubscribed
    };

    try {
      // Obtener token de LiveKit (similar a StreamContainer pero siempre como viewer)
      const identity = `preview-${Date.now()}`; // Identidad única para preview
      const response = await fetch(`${serverConfig.serverUrl}/livekit-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tokenAddress, identity, isOwner: false }), // Siempre isOwner: false
      });
      if (!response.ok) throw new Error(`Token request failed: ${await response.text()}`);
      const data = await response.json();

      console.log(logPrefix, "Connecting to room...");
      await room.connect(serverConfig.livekitWsUrl, data.token, { autoSubscribe: true });
      console.log(logPrefix, "Connected successfully.");

    } catch (err: any) {
      console.error(logPrefix, "Connection failed:", err);
      setError("Could not connect to stream.");
      setIsLoading(false);
      if (roomRef.current) {
        await roomRef.current.disconnect();
        roomRef.current = null;
      }
    }
  }, [tokenAddress, ownerAddress, logPrefix]); // Dependencias para conectar

  // Efecto para conectar/desconectar al montar/desmontar o cambiar props
  useEffect(() => {
    connectToRoom();

    // Cleanup: desconectar al desmontar
    return () => {
      console.log(logPrefix, "Cleanup: Disconnecting room.");
      roomRef.current?.disconnect();
      roomRef.current = null;
    };
  }, [connectToRoom]); // Depende de la función estable connectToRoom

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    const room = roomRef.current;
    if (!room) return;

    // Listen for owner's video track subscription
    const subHandler = (_track, _pub, participant) => {
      if (participant.identity === ownerAddress) {
        setHasTrack(true);
        clearTimeout(timeoutId);
      }
    };
    room.on('trackSubscribed', subHandler);

    // After 5s, if no video track arrived, remove this card
    timeoutId = setTimeout(() => {
      if (!hasTrack) {
        console.warn(`[PreviewCard ${tokenAddress.slice(0,6)}] no live track, marking inactive`);
        room.disconnect();
        onInactive?.();
      }
    }, 5000);

    return () => {
      clearTimeout(timeoutId);
      room.off('trackSubscribed', subHandler);
    };
  }, [ownerAddress, tokenAddress, onInactive, hasTrack]);

  // actualizar viewerCount cuando cambie isConnected
  useEffect(() => {
    const room = roomRef.current;
    if (!room) return;
    const update = () => setViewerCount(room.remoteParticipants.size);
    update();
    room.on(RoomEvent.ParticipantConnected, update);
    room.on(RoomEvent.ParticipantDisconnected, update);
    return () => {
      room.off(RoomEvent.ParticipantConnected, update);
      room.off(RoomEvent.ParticipantDisconnected, update);
    };
  }, [isConnected]);

  // Opcional: Fetch para obtener más info del token si no viene de la API principal
  // useEffect(() => {
  //   const fetchTokenDetails = async () => {
  //     // Lógica para buscar nombre/símbolo del tokenAddress si es necesario
  //     // setTokenInfo({ name: 'TokenName', symbol: 'TKN' });
  //   };
  //   if (!initialTokenName) { // Solo buscar si no vino de la API
  //      fetchTokenDetails();
  //   } else {
  //      setTokenInfo({ name: initialTokenName, symbol: '?' }); // Usar nombre inicial
  //   }
  // }, [tokenAddress, initialTokenName]);

  // Use the chainId prop instead of hardcoding
  // const chainId = 97; // <-- Remove hardcoded value
  const tokenLogoUrl = `${imageUrl}logo/${projectName}-${chainId}-${tokenAddress}.png`;
  // Actualizar la URL para la nueva página de stream
  const streamViewUrl = `/stream/${tokenAddress}`;
  // const buyPageUrl = `/buy/?chain=${chainId}&address=${tokenAddress}`; // Keep this if needed elsewhere

  return (
    <div className="relative overflow-hidden"> {/* contenedor para overlays */}
      {/* LIVE badge */}
      <div className="absolute top-2 left-2 z-20 bg-black bg-opacity-60 text-white text-xs px-2 py-0.5 rounded inline-flex items-center">
        <span className="animate-pulse text-green-400 mr-1">●</span> LIVE
      </div>

      {/* Viewer count */}
      <div className="absolute top-2 right-2 z-20 bg-black bg-opacity-60 text-white text-xs px-2 py-0.5 rounded inline-flex items-center">
        <FaEye className="mr-1" /> {viewerCount}
      </div>

      <div className="bg-[#050f1a] rounded-[10px] overflow-hidden shadow-lg border border-gray-700 flex flex-col">
        <div className="relative flex items-center justify-center bg-black aspect-video">
          <video
            ref={videoRef}
            playsInline
            autoPlay
            muted // Importante para autoplay en navegadores
            className={`w-full h-full object-cover ${showVideo ? 'block' : 'hidden'}`}
          />
          {isLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-400">
              <FaSpinner className="mb-2 text-3xl animate-spin" />
              <span>Loading Preview...</span>
            </div>
          )}
          {!isLoading && !showVideo && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
              <FaEyeSlash className="mb-2 text-4xl" />
              <span>{error || 'Preview Unavailable'}</span>
            </div>
          )}
          {/* Podrías añadir overlay con viewer count si lo tienes */}
          {/* {initialViewerCount !== undefined && (
              <div className="absolute z-10 px-2 py-1 text-xs text-white bg-black bg-opacity-50 rounded top-2 right-2">
                  👤 {initialViewerCount}
              </div>
          )} */}
        </div>
        <div className="p-4">
          <div className="flex items-center mb-2">
            <img
              src={tokenLogoUrl}
              alt="Token Logo"
              className="w-8 h-8 mr-3 rounded-full"
              onError={(e) => { e.currentTarget.src = '/img/logo.png'; }} // Fallback logo
            />
            <div>
              {/* Muestra nombre del token si lo tienes, sino la dirección */}
              <p className="text-lg font-semibold leading-tight">
                {/* {tokenInfo?.name || initialTokenName || `Token ${tokenAddress.slice(0, 6)}`} */}
                Token {tokenAddress.slice(0, 6)}...
              </p>
              {/* <p className="text-sm text-gray-400">{tokenInfo?.symbol || coinNames[chainId]}</p> */}
              <p className="text-sm text-gray-400">{coinNames[chainId] || `Chain ${chainId}`}</p>
            </div>
          </div>
          <p className="mb-3 text-xs text-gray-500 break-all">
            Owner: {ownerAddress.slice(0, 6)}...{ownerAddress.slice(-4)}
          </p>
          <Link
            // Apuntar a la nueva ruta de visualización del stream
            to={{
                pathname: streamViewUrl,
                state: { chainId, ownerAddress }
            }}
            className="block w-full text-center bg-gradient-to-r from-[#1c5290] to-[#45c0be] hover:opacity-90 text-white font-bold py-2 px-4 rounded-[5px] transition duration-300"
          >
            Watch Stream {/* Cambiar texto del botón */}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default StreamPreviewCard;
